/**
 * Frontend Logger
 * Structured logging for React Native/Expo applications
 * Replaces console.log/error/warn with proper logging mechanism
 */

import { Platform } from 'react-native';

// Log levels
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

// Determine if we're in development
const isDevelopment = 
  process.env.NODE_ENV !== 'production' && 
  (typeof __DEV__ === 'undefined' || __DEV__);

// Get log level from environment or default
const getLogLevel = (): LogLevel => {
  const envLevel = process.env.EXPO_PUBLIC_LOG_LEVEL?.toUpperCase();
  if (envLevel === 'DEBUG') return LogLevel.DEBUG;
  if (envLevel === 'INFO') return LogLevel.INFO;
  if (envLevel === 'WARN') return LogLevel.WARN;
  if (envLevel === 'ERROR') return LogLevel.ERROR;
  if (envLevel === 'NONE') return LogLevel.NONE;
  
  // Default: debug in dev, warn in production
  return isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;
};

const currentLogLevel = getLogLevel();

// Log entry structure
interface LogEntry {
  level: string;
  message: string;
  timestamp: string;
  platform: string;
  context?: string;
  data?: any;
  error?: {
    message: string;
    stack?: string;
    name?: string;
  };
}

// Format log entry
function formatLogEntry(
  level: string,
  message: string,
  context?: string,
  data?: any,
  error?: Error
): LogEntry {
  return {
    level,
    message,
    timestamp: new Date().toISOString(),
    platform: Platform.OS,
    ...(context && { context }),
    ...(data && { data }),
    ...(error && {
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name,
      },
    }),
  };
}

// Send to Sentry if available (for errors)
function sendToSentry(entry: LogEntry) {
  if (entry.level === 'ERROR' && typeof window !== 'undefined') {
    try {
      // Try to use Sentry if available (optional dependency)
      // @ts-ignore - Sentry may not be installed
      if (typeof window !== 'undefined' && (window as any).Sentry && entry.error) {
        // @ts-ignore
        (window as any).Sentry.captureException(new Error(entry.error.message), {
          extra: {
            context: entry.context,
            data: entry.data,
            platform: entry.platform,
          },
        });
      }
    } catch (e) {
      // Silently fail - Sentry might not be configured
    }
  }
}

// Output log based on platform
function outputLog(entry: LogEntry) {
  const logString = JSON.stringify(entry, null, isDevelopment ? 2 : 0);
  
  switch (entry.level) {
    case 'ERROR':
      console.error(`[${entry.level}]`, entry.message, entry.error || entry.data || '');
      if (isDevelopment) {
        console.error('Full log entry:', logString);
      }
      break;
    case 'WARN':
      console.warn(`[${entry.level}]`, entry.message, entry.data || '');
      if (isDevelopment) {
        console.warn('Full log entry:', logString);
      }
      break;
    case 'INFO':
      console.info(`[${entry.level}]`, entry.message, entry.data || '');
      break;
    case 'DEBUG':
      console.log(`[${entry.level}]`, entry.message, entry.data || '');
      break;
  }
}

// Main logger class
class Logger {
  private shouldLog(level: LogLevel): boolean {
    return level >= currentLogLevel;
  }

  debug(message: string, context?: string, data?: any) {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    const entry = formatLogEntry('DEBUG', message, context, data);
    outputLog(entry);
  }

  info(message: string, context?: string, data?: any) {
    if (!this.shouldLog(LogLevel.INFO)) return;
    const entry = formatLogEntry('INFO', message, context, data);
    outputLog(entry);
  }

  warn(message: string, context?: string, data?: any) {
    if (!this.shouldLog(LogLevel.WARN)) return;
    const entry = formatLogEntry('WARN', message, context, data);
    outputLog(entry);
  }

  error(message: string, error?: Error | any, context?: string, data?: any) {
    if (!this.shouldLog(LogLevel.ERROR)) return;
    
    // Handle both Error objects and plain error data
    const errorObj = error instanceof Error ? error : 
                     error ? new Error(String(error)) : undefined;
    
    const entry = formatLogEntry('ERROR', message, context, data, errorObj);
    outputLog(entry);
    
    // Send to Sentry in background (don't block)
    try {
      sendToSentry(entry);
    } catch (e) {
      // Silently fail
    }
  }

  // Convenience method for API requests
  logRequest(method: string, url: string, status?: number, duration?: number) {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    this.debug('API Request', 'api', {
      method,
      url,
      status,
      duration: duration ? `${duration}ms` : undefined,
    });
  }

  // Convenience method for API errors
  logApiError(method: string, url: string, error: any) {
    this.error(
      `API Error: ${method} ${url}`,
      error,
      'api',
      {
        method,
        url,
        status: error?.status,
        message: error?.message,
      }
    );
  }
}

// Export singleton instance
export const logger = new Logger();

// Export default for convenience
export default logger;

